import type { Message } from "./sync/types";
import { locale, t, tn } from "./i18n";

// How a channel's messages are laid out: a divider for each day, and a
// follow-up from the same author soon after shown without their name again.

// Follow-ups this close to the message before them are grouped with it.
export const groupWindowMs = 5 * 60 * 1000;

export function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// dayLabel names a day the way people say it: Today, Yesterday, or the date.
export function dayLabel(d: Date, now = new Date()) {
  if (sameDay(d, now)) return t("Today");
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return t("Yesterday");
  return d.toLocaleDateString(locale, { weekday: "long", month: "long", day: "numeric", ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }) });
}

// lasted says how long something went on, as a call.
export function lasted(ms: number) {
  const m = Math.round(ms / 60_000);
  if (m < 1) return t("under a minute");
  if (m < 60) return tn(m, "{n} minute", "{n} minutes");
  return t("{h} h {m} min", { h: Math.floor(m / 60), m: m % 60 });
}

// grouped is whether m follows prev closely enough to drop its header.
export function grouped(prev: Message | undefined, m: Message) {
  if (!prev || prev.author.id !== m.author.id || prev.class === "ephemeral" || prev.on_behalf_of !== m.on_behalf_of) return false;
  const a = new Date(prev.created_at);
  const b = new Date(m.created_at);
  return sameDay(a, b) && b.getTime() - a.getTime() < groupWindowMs;
}

export type Row =
  | { kind: "day"; key: string; label: string }
  | { kind: "new"; key: string }
  | { kind: "message"; key: string; m: Message; compact: boolean };

// rows lays out messages with day dividers and, when lastRead is given, a
// "New" line before the first message after it that someone else wrote.
export function rows(messages: Message[], lastRead: number | null, me: string | undefined, now = new Date()): Row[] {
  const out: Row[] = [];
  let prev: Message | undefined;
  let marked = false;
  for (const m of messages) {
    const d = new Date(m.created_at);
    const newDay = !prev || !sameDay(new Date(prev.created_at), d);
    if (newDay) out.push({ kind: "day", key: "day-" + d.toDateString(), label: dayLabel(d, now) });
    let isNew = false;
    if (!marked && lastRead !== null && m.seq > lastRead && m.author.id !== me) {
      out.push({ kind: "new", key: "new" });
      marked = isNew = true;
    }
    out.push({ kind: "message", key: m.id, m, compact: !newDay && !isNew && grouped(prev, m) });
    prev = m;
  }
  return out;
}
